import {
  Arrange,
  Button,
  FormField,
  Icon,
  Radio,
  Select,
  Spacer,
  SvgArrowRight,
  SvgSparkle,
  Text,
} from '@loomhq/lens';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { updateWelcomeRole, updateWelcomeUseCasePlan } from 'actions/welcome';
import { ONBOARDING_JOB_ROLES_WITH_MESSAGE } from 'constants/employmentRoles';
import { COMPLETED_ONBOARDING_STEP } from 'constants/events';
import { SLACKID_ONBOARDING_MODAL } from 'constants/onboarding';
import { patchUserPersonaInfoCreator } from 'creators/welcome';
import * as analytics from 'utilities/analytics';

import {
  MAP_CLEARBIT_TO_USE_CASE,
  MAP_USE_CASE_TO_CLEARBIT,
  USE_CASE_EDUCATION,
  USE_CASE_OPTIONS,
  USE_CASE_PERSONAL,
  USE_CASE_WORK,
} from '../../../welcome-webapp-simplified/steps/persona-use-case-step';

import { PERSONA_USE_CASE_STEP } from 'components/welcome-webapp-simplified/constants';

import styles from './styles.module.less';

const USE_CASE_MESSAGE = {
  [USE_CASE_EDUCATION]: 'Teachers often use Loom to record lesson plans.',
  [USE_CASE_PERSONAL]: 'People often use Loom to record thank you notes.',
};

function JobDescriptionPicker({ currentCompanyRole }) {
  const dispatch = useDispatch();

  const initialMessage = currentCompanyRole
    ? ONBOARDING_JOB_ROLES_WITH_MESSAGE.find(
        option => option.role === currentCompanyRole
      )?.message
    : null;

  const [message, setMessage] = useState(initialMessage);

  const onChange = e => {
    const selectedOption = ONBOARDING_JOB_ROLES_WITH_MESSAGE.find(
      option => option.role === e.value
    );

    dispatch(updateWelcomeRole(e.value));
    setMessage(selectedOption.message);
  };

  return (
    <>
      <Text fontWeight="medium" color="grey8">
        Which role best describes what you do?
      </Text>
      <Spacer bottom="xsmall" />
      <Select
        placeholder="Select your role"
        onChange={onChange}
        selectedOptionValue={currentCompanyRole}
        options={ONBOARDING_JOB_ROLES_WITH_MESSAGE.map(option => {
          return { value: option.role, title: option.role };
        })}
      />
      {message && (
        <Spacer top="small">
          <div className="flex">
            <Spacer right={1} top="0.2rem">
              <Icon icon={<SvgSparkle />} color="grey6" size={2} />
            </Spacer>
            <Text color="grey6">{message}</Text>
          </div>
        </Spacer>
      )}
    </>
  );
}

function PersonaDetails({ persona, currentCompanyRole }) {
  return (
    <Spacer left={3.5} top="small">
      {persona === USE_CASE_WORK ? (
        <JobDescriptionPicker currentCompanyRole={currentCompanyRole} />
      ) : (
        <Text color="grey6">{USE_CASE_MESSAGE[persona]}</Text>
      )}
    </Spacer>
  );
}

function PersonaPicker({ persona, setPersona, currentCompanyRole }) {
  return (
    <Arrange autoFlow="row" gap={1}>
      {USE_CASE_OPTIONS.map(useCase => {
        const isSelected = persona === useCase;

        return (
          <div key={useCase}>
            <FormField direction="row">
              <Radio
                id={useCase}
                name={useCase}
                isChecked={isSelected}
                value={useCase}
                onChange={() => setPersona(useCase)}
              />
              <label htmlFor={useCase}>
                <Spacer left="small">
                  <Text size="medium">{useCase}</Text>
                </Spacer>
              </label>
            </FormField>
            {isSelected && (
              <div className={styles.personaDetailShiftLeft}>
                <PersonaDetails
                  persona={persona}
                  currentCompanyRole={currentCompanyRole}
                />
              </div>
            )}
          </div>
        );
      })}
    </Arrange>
  );
}

function PersonaRoleStep({ handleNextStep }) {
  const dispatch = useDispatch();

  const selectedPersona = useSelector(state => state.welcome);

  // Maps work -> At work
  const [persona, setPersona] = useState(
    MAP_CLEARBIT_TO_USE_CASE[selectedPersona.use_case_plan] || ''
  );

  const currentCompanyRole = useSelector(state => state.welcome.role);

  useEffect(() => {
    dispatch(updateWelcomeUseCasePlan(MAP_USE_CASE_TO_CLEARBIT[persona]));

    if (persona !== USE_CASE_WORK) {
      dispatch(updateWelcomeRole(null));
    }
  }, [dispatch, persona]);

  function handleSubmit() {
    const personaBody = {
      use_case_plan: MAP_USE_CASE_TO_CLEARBIT[persona], // Ex: work
      use_case_plan_persona: persona, // Ex: At work
      persona_role: persona === USE_CASE_WORK ? currentCompanyRole : null, // Ex: Engineering, Marketing, etc.
    };

    dispatch(
      patchUserPersonaInfoCreator({
        ...personaBody,
      })
    );

    analytics.track(COMPLETED_ONBOARDING_STEP, {
      ...personaBody,
      step: PERSONA_USE_CASE_STEP,
      source: SLACKID_ONBOARDING_MODAL,
    });

    handleNextStep();
  }

  const disableNext =
    persona === '' || (persona === USE_CASE_WORK && !currentCompanyRole);

  return (
    <>
      <Text size="large" fontWeight="bold">
        How will you use Loom?
      </Text>
      <Spacer bottom="small" />
      <Text size="medium">
        We will provide helpful resources and videos to help you get started.
      </Text>
      <Spacer bottom="large" />
      <PersonaPicker
        persona={persona}
        setPersona={setPersona}
        currentCompanyRole={currentCompanyRole}
      />
      <Spacer bottom="large" />
      <div className="flex justify:flexEnd">
        <Button
          isDisabled={disableNext}
          icon={<SvgArrowRight />}
          iconPosition="right"
          onClick={handleSubmit}
        >
          Next
        </Button>
      </div>
    </>
  );
}

export default PersonaRoleStep;
